// BatiAssistCRMPage.jsx
// Page Mon Cockpit dans le contexte Espace Pro Abracadabati
// Version sans Header/Footer BatiAssist

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Building2, Users, FileText, LayoutDashboard, 
  CheckCircle, ArrowRight, XCircle, Lightbulb,
  PieChart, FolderKanban, Receipt
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const problems = [
  "Logiciels compliqués avec trop de fonctionnalités",
  "Interfaces peu intuitives",
  "Outils conçus par des développeurs, pas des artisans",
  "Perte de temps à comprendre le logiciel",
];

const solutions = [
  { icon: Users, text: "Gérer tes prospects et clients" },
  { icon: FolderKanban, text: "Suivre tes chantiers en temps réel" },
  { icon: FileText, text: "Créer tes devis en quelques clics" },
  { icon: Receipt, text: "Gérer tes factures facilement" },
  { icon: PieChart, text: "Analyser ton activité" },
];

const benefits = [
  "Interface simple et intuitive",
  "Conçu pour le terrain",
  "Prise en main immédiate",
  "Support réactif",
];

export default function BatiAssistCRMPage() {
  return (
    <ImmersiveEspaceProWrapper>
      <div className="min-h-screen bg-white" data-testid="batiassist-crm-page">
        {/* Header Abracadabati */}
        <UnifiedHeader />
        
        
        {/* Sous-navigation Espace Pro */}
        <EspaceProNav />
      
      {/* Contenu CRM Bâtiment */}
      <main>
        {/* Hero */}
        <section className="pt-16 pb-16 md:pt-20 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
                <Building2 className="w-4 h-4" />
                Mon Cockpit
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
                Le Cockpit créé par un{' '}
                <span className="text-[#F97316]">entrepreneur du bâtiment</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-8 leading-relaxed">
                BatiAssist a été conçu par un entrepreneur ayant construit plus de <strong>1500 piscines dans 7 pays</strong>. 
                Après avoir utilisé de nombreux logiciels complexes et coûteux, il a décidé de créer le Cockpit qu'il aurait aimé avoir.
              </p>
              <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-10">
                Un outil <strong>simple</strong>, <strong>rapide</strong> et <strong>adapté à la réalité du terrain</strong>.
              </p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                  Découvrir Mon Cockpit BatiAssist
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Tu en as marre des logiciels <span className="text-red-500">compliqués</span> ?
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Les outils classiques sont souvent conçus par des développeurs qui n'ont jamais mis les pieds sur un chantier.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {problems.map((problem, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-3 p-4 bg-red-50 rounded-xl"
                >
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <span className="text-slate-700">{problem}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                BatiAssist : l'outil <span className="text-[#F97316]">pensé pour toi</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Un Cockpit simple et efficace qui te permet de tout gérer au même endroit.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {solutions.map((solution, idx) => {
                const Icon = solution.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#1E3A5F]" />
                    </div>
                    <span className="font-medium text-slate-700">{solution.text}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full mb-6">
                <Lightbulb className="w-4 h-4" />
                <span className="text-sm font-semibold">Pourquoi choisir BatiAssist ?</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Un Cockpit <span className="text-[#F97316]">fait pour le terrain</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {benefits.map((benefit, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-3 p-4 bg-green-50 rounded-xl"
                >
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F]">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Prêt à simplifier ta gestion ?
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                Réserve ton diagnostic gratuit de 60 minutes et découvre comment BatiAssist peut transformer ton quotidien.
              </p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold rounded-full px-10 py-6 text-lg shadow-xl">
                  Réserver mon diagnostic gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      </div>
    </ImmersiveEspaceProWrapper>
  );
}
